import type { IFlattenedCategory } from "app/common/services/categoryService.types";
import { useApiQuery } from "app/modules/api-client";
import type { IIndustryDataAvailabilityState } from "app/pages/digital-marketing/ad-intelligence/common/data-availability/industryDataAvailability";
import { INDUSTRY_ENDPOINT_MIN_DATE } from "app/pages/digital-marketing/ad-intelligence/common/data-availability/industryDataAvailability";

import { useIndustryAvailabilityParams } from "../../overview/industry/useIndustryAvailabilityParams";
import type { IndustrySortType } from "../adsSortOptions";
import type { AdsGalleryData } from "../hooks/useAdsGallery";

import { industryAdToGalleryData } from "./industryAdToGalleryData";

interface IUseIndustryAdsGalleryResult {
    data: AdsGalleryData[];
    isLoading: boolean;
    isError: boolean;
    totalCount: number;
    availability: IIndustryDataAvailabilityState;
}

export interface IIndustryAdsGalleryFilters {
    campaignPages?: string[];
    advertisers?: string[];
    publishers?: string[];
    creativeTypes?: string[];
}

export const useIndustryAdsGallery = (
    category: IFlattenedCategory,
    page: number,
    pageSize: number,
    sort?: IndustrySortType,
    filters?: IIndustryAdsGalleryFilters,
): IUseIndustryAdsGalleryResult => {
    const { params, availability } = useIndustryAvailabilityParams(
        category,
        INDUSTRY_ENDPOINT_MIN_DATE.adintelligenceIndustryRecentAdsList,
    );

    const { data, isLoading, isFetching, isError } = useApiQuery(
        {
            tag: "AdIntelligenceMarketingAssets",
            methodName: "adintelligenceIndustryRecentAdsList",
            options: {
                enabled: !availability.isFullyUnavailable,
                select: (response) => ({
                    data: (response?.data ?? []).map(industryAdToGalleryData),
                    totalCount: response?.totalCount ?? 0,
                }),
            },
        },
        {
            ...params,
            page,
            pageSize,
            sort: sort ?? "last_seen",
            ...(filters?.campaignPages?.length ? { campaignPages: filters.campaignPages } : {}),
            ...(filters?.advertisers?.length ? { advertisers: filters.advertisers } : {}),
            ...(filters?.publishers?.length ? { publishers: filters.publishers } : {}),
            ...(filters?.creativeTypes?.length ? { creativeTypes: filters.creativeTypes } : {}),
        },
    );

    return {
        data: data?.data ?? [],
        totalCount: data?.totalCount ?? 0,
        isLoading: isLoading || isFetching,
        isError,
        availability,
    };
};
